import React, { useEffect, useState } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { useTracker } from 'infrastructure/hooks/shared/useTracker';
import { getTrackerTitle } from 'infrastructure/hooks/shared/useTracker/getTrackerTitle';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import DigitalEngagementSelectBox from 'components/molecules/digital-engagement-select-box';
import MasterModal from 'components/organisms/masterModal';
import BaseTextArea from 'components/atoms/base-textarea';

import s from './styles.module.scss';
import { ContactTypeSelect } from './contactType';

import type { DigitalEngagementType } from 'infrastructure/data-sources/digital-engagement';
import type { ContactType } from './contactType';
import type { ISendSmsDataProps } from 'infrastructure/hooks/control-panel/api';

export interface SmsModalProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  loading: boolean;
  submit: (
    data: ISendSmsDataProps,
  ) => Promise<{ error: null | 'close alert' | (string & {}) }>;
  isRpmPanel: boolean;
}

const SmsModal: React.FC<SmsModalProps> = ({
  isOpen,
  toggle,
  patientGuid,
  loading,
  submit,
  isRpmPanel,
}) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState<ContactType>('primary');
  const [digitalEngagement, setDigitalEngagement] =
    useState<DigitalEngagementType | null>();
  const [isDEEmpty, setIsDEEmpty] = useState(false);

  const {
    time,
    secondsRef,
    startTracker,
    stopTracker,
    createTrackerLoading,
    trackerGuid,
    deleteTracker,
    resumeTracker,
  } = useTracker();

  useEffect(() => {
    if (isRpmPanel && isOpen) {
      startTracker();
    }
  }, [isRpmPanel, isOpen]);

  const handleSubmit = async () => {
    if (digitalEngagement === undefined) {
      setIsDEEmpty(true);
      return;
    }

    if (isDEEmpty) {
      setIsDEEmpty(false);
    }

    stopTracker();
    const postData: ISendSmsDataProps = {
      patientGuid,
      topic: '',
      type: 'sms',
      message,
      isRpmPanel,
      isPrimaryNumber: contact === 'primary',
      timerDuration: isRpmPanel ? secondsRef.current : 0,
      trackerGuid: isRpmPanel ? trackerGuid : null,
      digitalEngagement: digitalEngagement ?? undefined,
    };
    const { error } = await submit(postData);
    if (error === 'close alert') {
      resumeTracker();
      return;
    }
    if (!error) {
      showResult();
      toggle();
      return;
    }
    if (isRpmPanel && isOpen) {
      startTracker();
    }
  };

  const rightTitle = getTrackerTitle({
    isMinimized,
    trackerTimer: time,
  });

  return (
    <>
      {isOpen && (
        <MasterModal
          title={t('labels.sendSms')}
          rightTitle={isRpmPanel ? rightTitle : undefined}
          isOpen={isOpen}
          toggle={() => {
            toggle();
            deleteTracker();
          }}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          onSave={handleSubmit}
          isSaveDisabled={!message || createTrackerLoading}
          isSaveLoading={loading}
          saveLabel={t('controls.send')}
        >
          <div className={classNames(s['modal-content'], 'sms-popup-content')}>
            <div>
              <BaseTextArea
                className="sms-textarea"
                id="message"
                value={message}
                onChange={(message) => setMessage(message)}
                rows={6}
                placeholder={t('labels.typeOrSelectOption')}
              />
              <ContactTypeSelect
                patientGuid={patientGuid}
                onChange={setContact}
                value={contact}
              />
              <DigitalEngagementSelectBox
                placeholder={t('digitalEngagement.digitalEngagement')}
                id="digital_engagement"
                dataCy="digital-engagement-select-box"
                value={digitalEngagement}
                onChange={(v) => setDigitalEngagement(v)}
                withNull
                defaultValue={undefined}
                invalid={isDEEmpty}
              />
            </div>
          </div>
        </MasterModal>
      )}
    </>
  );
};

export default SmsModal;
